import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Bookmark, FileText, ChevronDown, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { FeedFilters } from "./FeedFilters";
import type { FilterState } from "./FeedFilters";
import { Popover } from "./Popover";
import { Menu } from "./Menu";
import { createExport, fetchSavedPosts } from "../../api/leaks";

interface FeedTopSectionProps {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  onClear: () => void;
}

export function FeedTopSection({ filters, onChange, onClear }: FeedTopSectionProps) {
  const [savedCount, setSavedCount] = useState(0);
  const [exportLoading, setExportLoading] = useState(false);
  const [hasUnapplied, setHasUnapplied] = useState(false);

  useEffect(() => {
    fetchSavedPosts().then(res => setSavedCount(res.total)).catch(console.error);
  }, []);

  const handleNewExport = async () => {
    setExportLoading(true);
    try {
      await createExport("all", filters);
      toast.success("Export started. Check your Export history.");
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Export failed");
    } finally {
      setExportLoading(false);
    }
  };

  const handleRemoveChip = (key: keyof FilterState | string, targetType?: string, targetValue?: string) => {
    if (key === "targets" && targetType && targetValue) {
      if (filters.targets) {
        try {
          const parsed = JSON.parse(filters.targets);
          if (parsed[targetType]) {
            parsed[targetType] = parsed[targetType].filter((v: string) => v !== targetValue);
            if (parsed[targetType].length === 0) delete parsed[targetType];
            const str = Object.keys(parsed).length > 0 ? JSON.stringify(parsed) : undefined;
            onChange({ ...filters, targets: str });
          }
        } catch (e) {}
      }
    } else {
      onChange({ ...filters, [key as keyof FilterState]: undefined });
    }
  };

  const activeChips: { key: string; label: string; value: string; onRemove: () => void }[] = [];
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== "" && v !== false && k !== "search" && k !== "includeContent") {
      if (k === "targets") {
        try {
          const parsed = JSON.parse(v as string);
          Object.entries(parsed).forEach(([type, values]) => {
            (values as string[]).forEach(val => {
              activeChips.push({
                key: `target-${type}-${val}`,
                label: type,
                value: val,
                onRemove: () => handleRemoveChip("targets", type, val)
              });
            });
          });
        } catch (e) {}
      } else {
        const label = k.replace(/([A-Z])/g, ' $1').trim();
        activeChips.push({
          key: k,
          label: label,
          value: String(v),
          onRemove: () => handleRemoveChip(k as keyof FilterState)
        });
      }
    }
  });

  return (
    <>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="leaks-title text-white">Intelligence Feed</h1>
        </div>
        <div className="flex items-center gap-2 h-10">
          <button 
            title="Coming soon"
            className="h-10 px-3 flex items-center gap-2 text-sm font-medium text-slate-400 bg-slate-800/30 rounded-lg border border-slate-700/30 cursor-not-allowed"
          >
            KIOSK MODE <span className="text-[10px] uppercase font-bold bg-slate-700 text-slate-300 px-1.5 py-0.5 rounded">Coming soon</span>
          </button>
          
          <Link to="/leaks/saved" className="h-10 px-3 flex items-center gap-2 text-sm font-medium text-slate-300 hover:text-white bg-slate-800/50 hover:bg-slate-700/50 rounded-lg transition-colors border border-slate-700/50">
            <Bookmark size={14} /> SAVED POSTS
            {savedCount > 0 && <span className="bg-indigo-500 text-white text-xs px-1.5 py-0.5 rounded-full">{savedCount}</span>}
          </Link>
          
          <Popover trigger={
            <button className="h-10 px-3 flex items-center gap-2 text-sm font-medium text-slate-300 hover:text-white bg-slate-800/50 hover:bg-slate-700/50 rounded-lg transition-colors border border-slate-700/50 group ui-open:bg-slate-700/50">
              <FileText size={14} /> EXPORTS
              <span className="text-[10px] uppercase font-bold bg-indigo-500/20 text-indigo-400 px-1.5 py-0.5 rounded ml-1 group-hover:bg-indigo-500/30 transition-colors">New</span>
              <ChevronDown size={14} className="text-slate-500 group-hover:text-slate-400 transition-transform ui-open:rotate-180" />
            </button>
          }>
            <div className="p-2 w-48 flex flex-col gap-1">
              <button 
                onClick={handleNewExport}
                disabled={exportLoading}
                className="text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white rounded transition-colors disabled:opacity-50"
              >
                {exportLoading ? <Loader2 size={14} className="animate-spin inline mr-2" /> : null}
                New Export
              </button>
              <Link 
                to="/leaks/exports"
                className="text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white rounded transition-colors"
              >
                Export history
              </Link>
            </div>
          </Popover>
        </div>
      </div>

      <FeedFilters 
        filters={filters} 
        onChange={(f) => {
          onChange(f);
          setHasUnapplied(false);
        }} 
        onClear={onClear}
        hasUnapplied={hasUnapplied}
        setHasUnapplied={setHasUnapplied}
      />
      
      {activeChips.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5">
          {activeChips.map((chip) => (
            <span key={chip.key} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-xs text-indigo-300">
              <span className="capitalize">{chip.label}</span>: {chip.value}
              <button onClick={chip.onRemove} className="hover:text-indigo-100 transition-colors">
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </>
  );
}
